import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { CFImage, CFLink } from '../../../lib/types';
import { ArrowIcon } from '../../modules/Cards';
import Link, { AutoLink } from '../Link';
import { SmallTag, TagContainer } from '../Tag';

export interface CardProps {
    title: string;
    date?: Date | string;
    endDate?: Date;
    link: string | CFLink;
    image: CFImage | null;
    /** Will default to sensible values for 2/3 column grids based on `in2ColumnGrid` */
    sizes?: string;
    in2ColumnGrid?: boolean;
    tags: { content: React.ReactNode, color?: string, textColor?: string }[];
    children?: React.ReactNode;
    /** Specify if card should flip flex direction on during medium breakpoint.
      * Should be used for cards in 3-column grids */
    mediumFlip?: boolean;
    imagePosition?: string;
}

export const timeFormat = Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' });

const Placeholder = styled.div.attrs({ className: 'placeholder' })`
    display: flex;
    img:only-child {
      object-fit: contain;
      border: none;
      scale: 0.6;
    }

    background-color: var(--color-charcoal-v02);
`;

const CardWrapper = styled.div<{ flip?: boolean, in2ColumnGrid?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: white;
  height: 100%;
  border: 1px solid var(--theme-border-color);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${(props) => props.theme.shadows.hard};
  
  img, .placeholder {
    width: 100%;
    height: auto;
    aspect-ratio: 3 / 2;
    border-block-end: inherit;
    border-block-end-color: var(--theme-border-color);
    object-fit: cover;
  }

  &:hover {
    border-color: var(--theme-border-color-hover);
  }

  &, * {
    box-sizing: border-box;
    margin: 0;
  }

  ${({ flip }) => flip && css`
    @media (max-width: ${(props) => props.theme.breakpoints.large}) {
      flex-direction: row;

      img, .placeholder  {
        width: auto;
        max-width: 253px;
        border-inline-end: inherit;
        border-inline-end-color: var(--theme-border-color);
        border-block-end: unset;
      }
    }

    @media (max-width: ${(props) => props.theme.breakpoints.medium}) {
      flex-direction: column;

      img, .placeholder  {
        width: 100%;  
        max-width: unset;
        border-inline-end: unset;
        border-block-end: inherit; 
        border-block-end-color: var(--theme-border-color);       
        aspect-ratio: 2 / 1;
      }
    }
  `}

  ${({ in2ColumnGrid }) => in2ColumnGrid && css`
    img, .placeholder  {
      aspect-ratio: 2 / 1;
    }
  `}
`;

const CardContent = styled.div`
  color: var(--color-charcoal-v100);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: var(--color-charcoal-v60);
  flex: 1;
  padding: 24px;
  font-size: 15px;
  gap: 8px;
  min-height: 168px;
`;

const CardTitle = styled.h3`
  font-size: 21px;
  color: var(--color-charcoal-v100);
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex: 1;
`;

const CardDate = styled.div`
  font-size: 15px;
`;

const CardImage = styled(Image)<{$imagePosition: string}>`
  object-position: ${(props) => props.$imagePosition};
`;

export const ImagePlaceholder = () => (
  <Placeholder>
    <img src="/images/logo.svg" alt="" />
  </Placeholder>
);

export const formatDateOrRange = (date?: Date | string, endDate?: Date) => date && (typeof date === 'string' ? date : (endDate ? timeFormat.formatRange(new Date(date), new Date(endDate)) : timeFormat.format(new Date(date))));

const Card: React.FC<CardProps> = ({
  image, sizes, tags, title, children, date,
  mediumFlip, in2ColumnGrid, link, endDate, imagePosition,
}) => {
  const [dateStr, setDateStr] = useState<string | undefined>(undefined);
  useEffect(() => {
    // This doesn't always render the same on the server as on the client,
    // so we need to use this client side only
    setDateStr(formatDateOrRange(date, endDate));
  }, [date, endDate]);

  const cardVW = in2ColumnGrid ? '50vw' : '30vw';
  const realSizes = sizes || (
    mediumFlip
      ? `(max-width: 900px) 100vw, (max-width: 1200px) 253px, (max-width: 1500px) ${cardVW}, 414px`
      : `(max-width: 900px) 100vw, (max-width: 1200px) ${cardVW}, 518px`);
  const content = (
    <CardWrapper flip={mediumFlip} in2ColumnGrid={in2ColumnGrid}>
      {image ? (
        <CardImage
          src={image.url}
          alt={image.description}
          sizes={realSizes}
          width={image.width}
          height={image.height}
          // Transient prop, see https://styled-components.com/docs/api#transient-props
          $imagePosition={imagePosition || 'center'}
        />
      ) : <ImagePlaceholder />}
      <CardContent>
        {!!tags.length && (
        <TagContainer>
          {tags.map((tag, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <SmallTag key={i} color={tag.color || 'var(--color-bluescape-v20)'} textColor={tag.textColor}>{tag.content}</SmallTag>
          ))}
        </TagContainer>
        )}
        <CardTitle>
          {title}
        </CardTitle>
        <p>{children}</p>
        <CardFooter>
          <CardDate>
            {dateStr}
          </CardDate>
          <ArrowIcon />
        </CardFooter>
      </CardContent>
    </CardWrapper>
  );

  if (typeof link === 'string') {
    return (
      <Link href={link}>
        {content}
      </Link>
    );
  }

  return (
    <AutoLink {...link}>
      {content}
    </AutoLink>
  );
};

export default Card;
